import { Badge, Button, Card, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { ChecklistRow } from '@/api/analyses';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { STATUS_COLORS } from '@/theme/colors';
import { brand } from '@/theme/theme';

export interface ChecklistProps {
  items: Pick<ChecklistRow, 'kind' | 'required' | 'status'>[] | ChecklistRow[];
  variant: 'full' | 'uploader';
  onUpload?: (kind: string) => void;
  /** Continue the upload of the object already registered for the slot. */
  onContinue?: (objectId: string) => void;
}

/** D15/D16: required-data slots with done/pending badges; the uploader variant names no objects. */
export function Checklist({ items, variant, onUpload, onContinue }: ChecklistProps) {
  const { t } = useTranslation('analyses');
  const kindLabel = useKindLabel();
  if (items.length === 0) {
    return (
      <Card>
        <Text fz={14} c={brand.textMuted}>
          {t('checklist.empty')}
        </Text>
      </Card>
    );
  }
  return (
    <Card p={0}>
      <Stack gap={0}>
        {items.map((row, i) => {
          const done = row.status === 'filled';
          const inProgress = row.status === 'in_progress';
          const c = STATUS_COLORS[done ? 'done' : inProgress ? 'active' : 'pending'];
          const full = variant === 'full' ? (row as ChecklistRow) : null;
          const uploading = full?.pending_state === 'pending' || full?.pending_state === 'uploading';
          const badge = done ? t('checklist.done') : inProgress ? t(uploading ? 'checklist.uploading' : 'checklist.checking') : t('checklist.pending');
          return (
            <Group key={i} justify="space-between" px="md" py="sm" style={{ borderBottom: i < items.length - 1 ? `1px solid ${brand.border}` : undefined }}>
              <Group gap="sm">
                <Badge variant="outline" fz={11} styles={{ root: { color: c.color, backgroundColor: c.bg, borderColor: c.border } }}>
                  {badge}
                </Badge>
                <Text fz={14} fw={600}>
                  {kindLabel(row.kind)}
                </Text>
                {!row.required && (
                  <Text fz={12} c={brand.textMuted}>
                    {t('checklist.optional')}
                  </Text>
                )}
                {full?.filled_by_name && (
                  <Text fz={12} c={brand.textMuted}>
                    · {full.filled_by_name}
                  </Text>
                )}
              </Group>
              {inProgress && uploading && onContinue && full?.pending_object && (
                <Button size="xs" variant="default" onClick={() => onContinue(full.pending_object!)}>
                  {t('checklist.upload')}
                </Button>
              )}
              {!done && !inProgress && onUpload && (
                <Button size="xs" variant="default" onClick={() => onUpload(row.kind)}>
                  {t('checklist.upload')}
                </Button>
              )}
            </Group>
          );
        })}
      </Stack>
    </Card>
  );
}
